import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronRight,
  Folder,
  FolderOpen,
  Hash,
  Building2,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { connectorQueries } from '@/lib/api/queries/connector'
import type { ClickUpNode, ClickUpNodeType } from '@/lib/api/types'

export type ClickUpDocParentType = 'space' | 'folder' | 'list'

export interface ClickUpDocDestination {
  workspaceId: string
  parentId: string
  parentType: ClickUpDocParentType
  /** Human-readable path for the footer summary, e.g. "Era / Product / Release Notes". */
  label: string
}

interface BreadcrumbSegment {
  /** ClickUp node id; `null` denotes the root (workspaces list). */
  id: string | null
  name: string
  /** ClickUp type at this level — `undefined` at root. */
  type?: ClickUpNodeType
}

interface ClickUpDocDestinationPickerProps {
  connectorId: string
  selected: ClickUpDocDestination | null
  onSelect: (destination: ClickUpDocDestination) => void
}

const DESTINATION_TYPES: ReadonlySet<ClickUpNodeType> = new Set<ClickUpNodeType>([
  'space',
  'folder',
  'list',
])

function isDestinationType(type: ClickUpNodeType): type is ClickUpDocParentType {
  return DESTINATION_TYPES.has(type)
}

/**
 * Browse query parentType expected by /api/v1/connectors/clickup/browse.
 * At root we pass undefined; otherwise we pass the type of the current node.
 */
function browseParentType(seg: BreadcrumbSegment): string | undefined {
  if (seg.id == null) return undefined
  return seg.type
}

export function ClickUpDocDestinationPicker({
  connectorId,
  selected,
  onSelect,
}: ClickUpDocDestinationPickerProps) {
  const [path, setPath] = useState<BreadcrumbSegment[]>([
    { id: null, name: 'Workspace' },
  ])
  const current = path[path.length - 1]

  // Workspace id = first segment after root. Stays stable as user descends.
  const workspaceSeg = path.find((s) => s.type === 'workspace')
  const workspaceId = workspaceSeg?.id ?? undefined

  const browse = useQuery(
    connectorQueries.browseClickUp(
      connectorId,
      current.id ?? undefined,
      browseParentType(current),
    ),
  )

  const items: ClickUpNode[] = (browse.data ?? []).filter(
    (n) => n.type !== 'doc',
  )

  const navigateInto = (node: ClickUpNode) => {
    setPath((prev) => [...prev, { id: node.id, name: node.name, type: node.type }])
  }

  const navigateTo = (index: number) => {
    setPath((prev) => prev.slice(0, index + 1))
  }

  return (
    // `flex-1 min-h-0` lets this picker stretch to fill the remaining
    // DialogContent height (the parent already uses `flex flex-col max-h-[80vh]`)
    // AND shrink below content height so the inner scroll region can clip
    // the list instead of overflowing past the dialog footer.
    <div className="flex flex-1 min-h-0 flex-col gap-3">
      <Breadcrumb path={path} onNavigate={navigateTo} />

      <div className="flex-1 min-h-0">
        {browse.isPending ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 py-2 px-2">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="h-4 flex-1 max-w-[220px]" />
              </div>
            ))}
          </div>
        ) : browse.isError ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-sm text-muted-foreground mb-3">
              Couldn&apos;t load ClickUp workspace.
            </p>
            <Button variant="default" size="sm" onClick={() => browse.refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Building2 className="w-8 h-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              {current.id == null
                ? 'No workspaces available'
                : current.type === 'list'
                  ? 'This list has no further children. It can be the destination.'
                  : 'Nothing to show here'}
            </p>
          </div>
        ) : (
          <ScrollArea className="h-full pr-2">
            <ul role="listbox" aria-label="ClickUp destinations" className="flex flex-col">
              {items.map((node) => (
                <DestinationRow
                  key={node.id}
                  node={node}
                  workspaceId={workspaceId}
                  selected={selected}
                  onSelect={(parentType) => {
                    if (!isDestinationType(node.type)) return
                    // Destinations are space/folder/list — never the workspace
                    // itself — so by the time we reach here we've already
                    // descended past a workspace segment and `workspaceId` is
                    // guaranteed defined. Guard anyway in case the tree state
                    // ever drifts.
                    if (!workspaceId) return
                    const label = [
                      ...path
                        .filter((s) => s.id != null && s.type !== 'workspace')
                        .map((s) => s.name),
                      node.name,
                    ].join(' / ')
                    onSelect({
                      workspaceId,
                      parentId: node.id,
                      parentType,
                      label: label || node.name,
                    })
                  }}
                  onDrillDown={() => navigateInto(node)}
                />
              ))}
            </ul>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}

function Breadcrumb({
  path,
  onNavigate,
}: {
  path: BreadcrumbSegment[]
  onNavigate: (index: number) => void
}) {
  return (
    <nav aria-label="ClickUp navigation" className="flex flex-wrap items-center gap-1 text-sm">
      {path.map((seg, idx) => {
        const isLast = idx === path.length - 1
        return (
          <span key={`${seg.id ?? 'root'}-${idx}`} className="flex items-center gap-1">
            {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
            {isLast ? (
              <span className="font-medium">{seg.name}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(idx)}
                className="text-muted-foreground hover:text-foreground hover:underline"
              >
                {seg.name}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}

function DestinationRow({
  node,
  workspaceId,
  selected,
  onSelect,
  onDrillDown,
}: {
  node: ClickUpNode
  workspaceId: string | undefined
  selected: ClickUpDocDestination | null
  onSelect: (parentType: ClickUpDocParentType) => void
  onDrillDown: () => void
}) {
  const selectable = isDestinationType(node.type)
  const drillable = node.type !== 'list'
  const isSelected =
    selectable &&
    selected != null &&
    selected.parentId === node.id &&
    selected.parentType === (node.type as ClickUpDocParentType) &&
    (workspaceId ?? null) === (selected.workspaceId ?? null)

  return (
    <li
      role="option"
      aria-selected={isSelected}
      className={cn(
        'flex items-center gap-2 px-2 py-2 rounded-md border border-transparent',
        isSelected && 'border-primary bg-primary/5',
      )}
    >
      {selectable ? (
        <input
          type="radio"
          name="clickup-doc-destination"
          aria-label={`Use ${node.name} as destination`}
          checked={isSelected}
          onChange={() => onSelect(node.type as ClickUpDocParentType)}
          className="w-4 h-4 accent-primary"
        />
      ) : (
        <span className="w-4 h-4" aria-hidden />
      )}

      <NodeIcon type={node.type} expanded={drillable} />

      <button
        type="button"
        className={cn(
          'flex-1 text-left text-sm flex items-center gap-2',
          drillable ? 'hover:underline' : 'cursor-default',
        )}
        onClick={() => {
          if (drillable) {
            onDrillDown()
          } else if (selectable) {
            onSelect(node.type as ClickUpDocParentType)
          }
        }}
      >
        <span className="truncate">{node.name}</span>
        <span className="text-xs text-muted-foreground capitalize">{node.type}</span>
      </button>

      {drillable && (
        <ChevronRight
          className="w-4 h-4 text-muted-foreground shrink-0"
          aria-hidden
        />
      )}
    </li>
  )
}

function NodeIcon({ type, expanded }: { type: ClickUpNodeType; expanded: boolean }) {
  if (type === 'workspace') return <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
  if (type === 'list') return <Hash className="w-4 h-4 text-muted-foreground shrink-0" />
  return expanded ? (
    <FolderOpen className="w-4 h-4 text-muted-foreground shrink-0" />
  ) : (
    <Folder className="w-4 h-4 text-muted-foreground shrink-0" />
  )
}
